import type { OnlineDevice } from '@/@types/device';
import {
  LocationType,
  type Stop,
  type StopTime,
  type StopTimeData,
  type Trip,
} from '@/@types/gtfs-ressource';
import type { FormatedUpdate } from '@/@types/trip-updates';
import type { Vehicle } from '@/@types/vehicle';
import type { TagElement } from '@/components/ui/InfoTag.vue';
import { isStopGroupAccessible, isStopGroupNotAccessible } from '@/libs/helpers/stop-clusters';
import { crowdingLevel, type CrowdingLevel } from '@pysae/transit-core/helpers';

export enum TagValues {
  CANCELLED = 'tripCanceled',
  BICYLE = 'bicyle',
  DOUBLE_DECKER = 'double-decker',
  PLATFORM = 'platform',
  PRM = 'prm',
  NOT_PRM = 'notPrm',
  TRAFFIC = 'traffic',
  TRAFFIC_INFO_COUNT = 'trafficInfoCount',
  UPON_REQUEST = 'uponRequest',
  DELAYED = 'tripDelayed',
  EARLY = 'tripEarly',
  UNSERVED = 'unserved',
  UNSERVED_STOPS_COUNT = 'unservedStopsCount',
  TEMPORARY_STOPS_COUNT = 'temporaryStopsCount',
  DISPLACED_STOPS_COUNT = 'displacedStopsCount',
  LOW_CROWDING = 'lowCrowding',
  MODERATE_CROWDING = 'moderateCrowding',
  HIGH_CROWDING = 'highCrowding',
  VERY_HIGH_CROWDING = 'veryHighCrowding',
}

export function formatTags(
  stop?: Stop,
  stopTime?: StopTime | StopTimeData,
  tripUpdate?: FormatedUpdate,
  trip?: Trip,
): Array<TagElement> {
  const tags: Array<TagElement> = [];
  // PRIORITY HANDLED BY THIS METHOD
  // 1. Canceled - tripUpdate
  if (tripUpdate?.canceledTrip) tags.push({ tagType: TagValues.CANCELLED });
  // 2.  UnservedStop - tripUpdate
  if (tripUpdate?.unservedStops)
    tags.push({
      tagType: TagValues.UNSERVED_STOPS_COUNT,
      tagData: tripUpdate.unservedStops.content,
    });
  // 3. DisplacedStop - tripUpdate
  if (tripUpdate?.displacedStops)
    tags.push({
      tagType: TagValues.DISPLACED_STOPS_COUNT,
      tagData: tripUpdate.displacedStops.content,
    });
  // 4. TemporaryStop - tripUpdate
  if (tripUpdate?.temporaryStops)
    tags.push({
      tagType: TagValues.TEMPORARY_STOPS_COUNT,
      tagData: tripUpdate.temporaryStops.content,
    });
  // 5.  Delay - tripUpdate
  if (tripUpdate?.delayedTrip)
    tags.push({
      tagType: tripUpdate.delayedTrip.isNegativeDelay ? TagValues.EARLY : TagValues.DELAYED,
      tagData: tripUpdate.delayedTrip.contentDelayTime,
    });
  // 6. platform_code
  if (stop?.platform_code && stop.platform_code?.length > 0)
    tags.push({ tagType: TagValues.PLATFORM, tagData: stop.platform_code });
  // 7. upon request
  if ([2, 3].includes(stopTime?.pickup_type || -1)) tags.push({ tagType: TagValues.UPON_REQUEST });

  // 8. PMR (case trip : only check trip accessibility, if stop check trip & stop accessibility)
  // A 2 on either side is an explicit "not accessible": enough to say so, whatever the other side.
  if (stop?.wheelchair_boarding === 2 || trip?.wheelchair_accessible === 2)
    tags.push({ tagType: TagValues.NOT_PRM });
  else if (
    (!stop && trip?.wheelchair_accessible === 1) ||
    (stop?.wheelchair_boarding === 1 && trip?.wheelchair_accessible === 1)
  )
    tags.push({ tagType: TagValues.PRM });

  // 9. Bike allowed
  if (trip?.bikes_allowed === 1) tags.push({ tagType: TagValues.BICYLE });

  return tags;
}

/**
 * Accessibility tag of the stop itself. A declared station carries its own
 * accessibility; a merged stop is only as accessible as its least accessible
 * platform, and "not accessible" needs every platform to say so.
 */
export function getStopAccessibilityTag(stop: Stop, groupStops: Array<Stop | undefined>): TagElement | null {
  const isStation = stop.location_type === LocationType.STATION;
  if (isStation ? stop.wheelchair_boarding === 1 : isStopGroupAccessible(groupStops))
    return { tagType: TagValues.PRM };
  if (isStation ? stop.wheelchair_boarding === 2 : isStopGroupNotAccessible(groupStops))
    return { tagType: TagValues.NOT_PRM };
  return null;
}

/**
 * Resolve the crowding tag for the stop time of a given trip.
 *
 * Crowding must reflect the trip the vehicle is actually operating, never the
 * upcoming trips of the same block: in sequential service the bus empties out
 * at the end of a course, so its live load says nothing about the next ones.
 * The device is only exposed on `trip_id` when it operates that trip, so we
 * gate on `device.trip_id === tripId`.
 */
export function getCrowdingTag(
  device: OnlineDevice | undefined,
  vehicle: Vehicle | undefined,
  tripId: string,
): TagElement | null {
  if (!device || !vehicle?.capacity || device.trip_id !== tripId) return null;
  return getCrowdingRatioTag(device, vehicle.capacity);
}

const CROWDING_TAG_BY_LEVEL: Record<CrowdingLevel, TagValues> = {
  low: TagValues.LOW_CROWDING,
  moderate: TagValues.MODERATE_CROWDING,
  high: TagValues.HIGH_CROWDING,
  veryHigh: TagValues.VERY_HIGH_CROWDING,
};

export function getCrowdingRatioTag(device: OnlineDevice, capacity: number): TagElement | null {
  const crowdingRatio = device.load_percentage ?? ((device.vehicle_load ?? 0) / capacity) * 100;

  const level = crowdingLevel(crowdingRatio);
  return level ? { tagType: CROWDING_TAG_BY_LEVEL[level] } : null;
}
